<template>
  <div>
    <v-row>
      <v-col cols="6" class="pt-0">
        <v-row
          ><v-col cols="12">
            <span
              class="details-heading application-heading pull-left mr-2 mb-2"
              >{{ titleDisplay }}</span
            >
            <PulseAndRiskDefinitions
              :rating-id="selectedRatingId"
              :initial="'RR'"
              class="pull-left"
            ></PulseAndRiskDefinitions>
            <div class="clearfix"></div> </v-col
        ></v-row>
        <RiskRatingPrimary
          :risks="risks"
          :selectedPrimaryRisk="selectedPrimaryRisk"
          :selectedSubRisk="selectedSubRisk"
          @click="handleClickRiskRating"
          :useClick="interactive"
          :parentRatings="parentRatings"
        ></RiskRatingPrimary>
      </v-col>
      <v-col
        class="pt-0"
        cols="6"
        v-if="selectedPrimaryRisk && selectedPrimaryRisk.type && risksSub"
      >
        <v-row
          ><v-col cols="12">
            <span class="details-subheading application-heading"
              >{{ selectedPrimaryRisk.type }} Sub Ratings</span
            >
          </v-col></v-row
        >
        <div
          :class="{
            well: interactive,
          }"
        >
          <v-row
            dense
            v-for="risk in risksSub"
            :key="risk.id"
            class="subrisk-container mt-1 mr-1"
            :class="{
              'subrisk-container-interactive': interactive,
              'subrisk-container-active':
                selectedSubRisk && selectedSubRisk.id === risk.id,
            }"
            @click="handleClickSubRiskRating(risk)"
          >
            <v-col
              cols="5"
              class="py-1 subrisk-type-container application-heading"
              >{{ risk.type }}</v-col
            >
            <v-col
              cols="3"
              class="py-1 pr-0 subrisk-bar-container"
              :title="getRiskLevel(risk)"
            >
              <div
                :style="getSubRiskStyle(risk)"
                class="subrisk-bar mt-1"
              ></div>
            </v-col>
            <v-col
              cols="4"
              class="py-1 subrisk-bar-container"
              :title="getRiskLevel(risk)"
            >
              <div class="subrisk-text">
                {{ risk.level }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashOrderBy from "lodash/orderBy";

import { getRiskTypes } from "@/services/controlRisks/risks.service";
import { RISK_LEVELS } from "@/common/constants/controlRisks/risks";
import { LABELS } from "@/common/constants/labels";
import { hasPlusCyberAccess } from "@/services/user/user.service";

const EMITTED_ACTIONS = {
  CLICK: "click",
};

export default {
  name: "RiskRating",
  components: {
    RiskRatingPrimary: () =>
      import("@/components/controlRisks/risks/RiskRatingPrimary"),
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
  },
  props: {
    title: {
      type: String,
      required: false,
    },
    ratings: {
      type: Array,
      required: true,
    },
    parentRatings: {
      type: Array,
      required: false,
    },
    selectedPrimaryRisk: {
      type: Object,
      required: false,
    },
    selectedSubRisk: {
      type: Object,
      required: false,
    },
  },
  data() {
    const availableRiskTypes = [
      { type: "Political", id: 1 },
      { type: "Operational", id: 2 },
      { type: "Security", id: 3 },
    ];
    if (hasPlusCyberAccess()) {
      availableRiskTypes.push({ type: "Cyber", id: 5 });
    }

    return {
      availableRiskTypes: availableRiskTypes,
      riskTypes: null,
      risks: null,
      risksSub: null,
    };
  },
  computed: {
    titleDisplay() {
      return this.title ? this.title : "Country " + LABELS.cr_risk_ratings;
    },
    riskTypesPrimary() {
      if (this.riskTypes && Array.isArray(this.riskTypes)) {
        let rTypes = [];
        this.riskTypes.forEach((rt) => {
          if (!rt.parentId) {
            rTypes.push(rt);
          }
        });
        return rTypes;
      }
      return null;
    },
    selectedRatingId() {
      if (lodashGet(this.selectedSubRisk, "id")) {
        return this.selectedSubRisk.id;
      }
      if (lodashGet(this.selectedPrimaryRisk, "id")) {
        return this.selectedPrimaryRisk.id;
      }
      return null;
    },
    interactive() {
      return !!lodashGet(this, "$listeners.click");
    },
  },
  methods: {
    async getFilteredRiskTypes() {
      let rtArr = [];
      const riskTypes = await getRiskTypes();
      riskTypes.forEach((riskType) => {
        this.availableRiskTypes.forEach((rt) => {
          if (riskType.id === rt.id || riskType.parentId === rt.id) {
            rtArr.push(riskType);
          }
        });
      });
      return rtArr;
    },
    getRiskLevel(risk) {
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        return lodashGet(rating, "name");
      }
      return null;
    },
    getRiskStyle(risk) {
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        const color = lodashGet(rating, "bgColor");
        let top = `${50 - 10 * risk.value}px`;
        let height = `${10 * risk.value}px`;
        return {
          "background-color": color,
          top: top,
          height: height,
        };
      }
      return null;
    },
    getSubRiskStyle(risk) {
      let isSameAsParent = false;
      const id = risk.id;
      const level = risk.value;
      for (
        let i = 0;
        this.parentRatings && i < this.parentRatings.length;
        i++
      ) {
        let checkLevel = this.parentRatings[i].rating.level;
        let checkId = this.parentRatings[i].riskCategory.id;
        if (id === checkId && level === checkLevel) {
          isSameAsParent = true;
        }
      }
      if (risk && risk.value) {
        const rating = lodashFind(RISK_LEVELS, ["value", risk.value]);
        const color = isSameAsParent ? "#CBCBCB" : lodashGet(rating, "bgColor");
        let width = `${10 * risk.value}px`;
        return {
          "background-color": color,
          width: width,
        };
      }
      return null;
    },
    getSubRatings() {
      this.risksSub = null;
      if (this.selectedPrimaryRisk) {
        let riskTypesSub = [];
        this.riskTypes.forEach((rt) => {
          if (rt.parentId === this.selectedPrimaryRisk.id) {
            riskTypesSub.push(rt);
          }
        });

        let subRatings = [];
        for (let i = 0; i < this.ratings.length; i++) {
          const rating = this.ratings[i];
          if (
            lodashGet(rating, "riskCategory.id") &&
            lodashGet(rating, "rating.level")
          ) {
            riskTypesSub.forEach((riskType) => {
              if (rating.riskCategory.id === riskType.id) {
                subRatings.push({
                  id: riskType.id,
                  type: riskType.name.replace(/\//g, " / "),
                  value: rating.rating.level,
                  level: rating.rating.name,
                });
              }
            });
          }
        }
        subRatings = lodashOrderBy(subRatings, ["type"], ["asc"]);
        this.risksSub = subRatings;
      }
    },
    handleClickRiskRating(risk) {
      if (
        this.selectedPrimaryRisk &&
        risk &&
        risk.id === this.selectedPrimaryRisk.id
      ) {
        this.$emit(EMITTED_ACTIONS.CLICK, null, false);
      } else {
        this.$emit(EMITTED_ACTIONS.CLICK, risk, false);
      }
    },
    handleClickSubRiskRating(risk) {
      if (this.selectedSubRisk && risk && risk.id === this.selectedSubRisk.id) {
        this.$emit(EMITTED_ACTIONS.CLICK, null, true);
      } else {
        this.$emit(EMITTED_ACTIONS.CLICK, risk, true);
      }
    },
  },
  watch: {
    ratings: {
      async handler() {
        if (this.ratings && Array.isArray(this.ratings)) {
          this.riskTypes = await this.getFilteredRiskTypes();
          this.risks = [];
          for (let i = 0; i < this.ratings.length; i++) {
            const rating = this.ratings[i];
            if (
              lodashGet(rating, "riskCategory.id") &&
              lodashGet(rating, "rating.level")
            ) {
              this.riskTypesPrimary.forEach((riskType) => {
                if (rating.riskCategory.id === riskType.id) {
                  this.risks.push({
                    id: riskType.id,
                    type: riskType.name,
                    value: rating.rating.level,
                  });
                }
              });
            }
          }
          this.risks = lodashOrderBy(this.risks, ["id"], ["asc"]);
          this.getSubRatings();
        }
      },
      immediate: true,
    },
    selectedPrimaryRisk() {
      this.getSubRatings();
    },
    selectedSubRisk() {
      this.getSubRatings();
    },
  },
  beforeDestroy() {},
};
</script>

<style scoped>
.details-heading {
  font-size: 15px;
}
.details-subheading {
  font-size: 15px;
}
.subrisk-container {
  background-color: white;
  border-width: 0px;
  border-style: solid;
  border-color: rgb(185, 185, 185);
  border-radius: 4px;
}
.subrisk-container-interactive {
  cursor: pointer;
}
.subrisk-container-active {
  border-color: rgb(107, 107, 107) !important;
  border-width: 1px;
}
.subrisk-type-container {
  font-size: x-small;
}
.subrisk-bar-container {
  position: relative;
  top: 1px;
  width: 50px;
  height: 30px;
}
.subrisk-bar {
  height: 10px;
  border-radius: 10px;
}
.subrisk-text {
  font-size: 10px;
  top: 2px;
  left: 15px;
}
.well {
  background-color: rgb(231, 231, 231);
  border-radius: 5px;
  padding-left: 8px;
  padding-top: 1px;
  padding-bottom: 4px;
  margin-top: 8px;
}
</style>
